import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { 
  Award, 
  CheckCircle, 
  XCircle, 
  Clock,
  User,
  BookOpen,
  Loader2,
  AlertCircle
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const AdminCertificateRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/certificate-requests`, {
        withCredentials: true
      });
      setRequests(response.data);
    } catch (error) {
      toast.error("Failed to load certificate requests");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    setProcessing(requestId);
    try {
      await axios.post(`${API}/admin/certificate-requests/${requestId}/approve`, {}, {
        withCredentials: true
      });
      toast.success("Certificate approved and issued!");
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve certificate");
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (requestId) => {
    setProcessing(requestId);
    try {
      await axios.post(`${API}/admin/certificate-requests/${requestId}/reject`, {
        reason: rejectReason || "Request rejected by administrator"
      }, {
        withCredentials: true
      });
      toast.success("Certificate request rejected");
      setRejectReason("");
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject request");
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-violet-700" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="certificate-requests-page">
        <div>
          <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
            Certificate Requests
          </h1>
          <p className="text-stone-600 mt-2">Review and approve student certificate requests</p>
        </div>

        {requests.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Award className="h-16 w-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-stone-900 mb-2">No Pending Requests</h3>
              <p className="text-stone-600">
                There are no certificate requests waiting for approval.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => (
              <Card key={request.request_id} className="overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Award className="h-6 w-6 text-violet-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-stone-900">{request.user_name}</h3>
                        <p className="text-sm text-stone-600">{request.user_email}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <BookOpen className="h-4 w-4 text-stone-400" />
                          <span className="text-sm text-stone-700">{request.course_title}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Clock className="h-4 w-4 text-stone-400" />
                          <span className="text-sm text-stone-500">
                            Requested: {formatDate(request.requested_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge className="bg-orange-100 text-orange-700">
                        <Clock className="h-3 w-3 mr-1" />
                        Pending
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="flex gap-3 mt-6 pt-4 border-t">
                    <Button
                      onClick={() => handleApprove(request.request_id)}
                      disabled={processing === request.request_id}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`approve-${request.request_id}`}
                    >
                      {processing === request.request_id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve & Issue Certificate
                        </>
                      )}
                    </Button>
                    
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          disabled={processing === request.request_id}
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Reject Certificate Request</AlertDialogTitle>
                          <AlertDialogDescription>
                            Please provide a reason for rejection (optional).
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <Textarea
                          placeholder="Reason for rejection..."
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          className="mt-2"
                        />
                        <AlertDialogFooter>
                          <AlertDialogCancel onClick={() => setRejectReason("")}>
                            Cancel
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleReject(request.request_id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Reject Request
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info Box */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Certificate Approval Process</p>
              <p className="text-sm text-blue-700 mt-1">
                Students must complete all course videos before they can request a certificate.
                Once approved, the certificate is automatically generated and made available to the student.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminCertificateRequests;
